import React from 'react';
import PropTypes from 'prop-types';
import {
  getContentElement,
  createElementEntry,
  getElementComponent,
  isElementExcluded,
  isRootElement,
  isReviewerOnly,
  isValidFactory,
  transformElementValue,
} from './ContentElements';
import { computeLogic } from './ContentLogic';
import ContentDebugger from './ContentDebugger';
import ContentControlHOC from './ContentControlHOC';
import cloneDeep from 'lodash.clonedeep';
import findIndex from 'lodash.findindex';

class ContentParser extends React.Component {
  static propTypes = {
    /** Classname to be applied to Content Parser */
    className: PropTypes.string,
    /** Overrides all controls and present in in read only mode using `ReadOnlyControl` */
    readOnly: PropTypes.bool,
    /** Show/hide labels for each Control */
    showLabels: PropTypes.bool,
    /** Provide root number of columns for special circumstances */
    numberOfColumns: PropTypes.number,
    /** Exclude a list of controls from being rendered */
    excludeControls: PropTypes.array,
    /** Override default controls */
    overrideControls: PropTypes.object,
    /** PageID that ties into the SSI/applicant menu */
    pageId: PropTypes.number,
    /** Turn on content   */
    debug: PropTypes.bool,
    /** Access to outside functions that may be used in various controls (ie: updating a menu, sending API request) */
    actions: PropTypes.object,
    /** Callback fired when any dynamic elemenet's value is changed. fn({ formValues: object, elementUpdated: string, isFormComplete: bool }) */
    onChange: PropTypes.func,
    /** Switch mode admin to edit controls */
    parentReadOnly: PropTypes.bool,
  };

  static defaultProps = {
    parentReadOnly: true,
    showLabels: false,
    readOnly: false,
    debug: false,
    actions: {},
    numberOfColumns: 2,
  };

  state = {
    parserReady: false,
  };

  constructor(props) {
    super(props);
    this.parserState = {
      initCallbacks: [],
      elements: [],
      elementsIndex: {},
      logicIndex: {},
      formValues: {
        dataRequests: {},
        tables: {},
        rawElements: {},
        rawElementsIndex: {},
      },
      lookUps: {},
      rootNumberOfColumns: this.props.numberOfColumns,
      addressFields: {},
      countrySelected: '',
      sdkCountry: '',
      sdkDoctype: '',
      exactInput: '',
    };
  }

  componentDidMount() {
    const { data } = this.props;
    this.parseData(data);
    this.setState({ parserReady: true }, () => {
      this.updateNested();
      this.runInitCallbacks();
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.numberOfColumns !== this.parserState.rootNumberOfColumns) {
      this.setState({ parseReady: true }, this.updateNested);
      this.parserState.rootNumberOfColumns = this.props.numberOfColumns;
    } else if (this.props.hasError !== prevProps.hasError) {
      const { elements } = this.parserState;
      this.applyShowLogicUpdates(elements, elements, true);
      this.handleOnChange();
    }
  }

  handleOnChange = (elementUpdated = null) => {
    if (this.props.onChange) {
      this.props.onChange({ isFormComplete: this.checkFormComplete(), formValues: this.parserState.formValues, elementUpdated });
    }
  };

  updateNested = () => {
    const { elements } = this.parserState;
    this.applyShowLogicUpdates(elements, elements, true);
    this.handleOnChange();
    this.updateCountryIDDocType(elements);
  };

  updateCountryIDDocType = elements => {
    elements.forEach(elNode => {
      if (elNode.props.addressLookUpCountry === true) {
        let lookUpValue = this.getLookupValue(elNode.controlTypeName);
        let showLogicLookUp = '';
        if (lookUpValue) {
          showLogicLookUp = lookUpValue.reduce((acc, next) => {
            //let lookupIndex = next.lookupCode ? next.lookupCode : next.displayText;

            acc[next.lookUpBindValue] = next.lookupCode;
            return acc;
          }, {});
        }

        this.parserState = { ...this.parserState, countrySelected: showLogicLookUp[elNode.props.value] };
      }
      if (elNode.id.indexOf('ExpIDCountry') !== -1) {
        this.parserState = { ...this.parserState, sdkCountry: elNode.props.value };
      }
      if (elNode.id.indexOf('ExpIDType') !== -1 && elNode.props.isComponentVisible === true) {
        this.parserState = { ...this.parserState, sdkDoctype: elNode.props.value };
      }
    });
  };

  setExactInput = input => {
    this.parserState = { ...this.parserState, exactInput: input };
  };

  getAddressField = data => {
    if (!data) return;
    let addressFields = [];
    data.pageElements.forEach(ele => {
      if (ele.smartAddLookupName !== null) {
        addressFields.push(ele);
      }
    });
    return addressFields;
  };
  runInitCallbacks = () => {
    const { initCallbacks } = this.parserState;
    initCallbacks.forEach(fn => fn());
  };

  /*
   * TO BE REMOVED: Backend at this time has an issue with casing. The `lookUp` string on individual Controls do not match the lookUps keys.
   * this is a temporary fix around the issue
   */
  tempBackendFix = lookUps => {
    const firstUpper = string => string.charAt(0).toUpperCase() + string.slice(1);
    const keys = Object.keys(lookUps);

    keys.forEach(key => {
      lookUps[firstUpper(key)] = lookUps[key];
    });

    return lookUps;
  };

  parseData = data => {
    if (!data.pageElements) {
      console.error('ContentParser: missing provided property `pageElements`');
      return false;
    }
    this.parserState = {
      ...this.parserState,
      rootNumberOfColumns: this.parserState.rootNumberOfColumns || data.numberOfColumns,
      lookUps: this.tempBackendFix(data.lookUps) || {},
      initCallbacks: [],
      profileCountryCode: this.parserState.profileCountryCode || data.profileCountryCode,
    };

    const elements = this.traverseElements(data.pageElements, cloneDeep(this.parserState.elements));
    const { elementsIndex, logicIndex } = this.createIndexes(elements);

    const evaluatedElements = this.evaluateComputeLogic(elements, elementsIndex, logicIndex);
    this.parserState = { ...this.parserState, elements: evaluatedElements, elementsIndex, logicIndex };
    this.setFormValues();
    this.parserState = {
      ...this.parserState,
      addressFields: this.getAddressField(data),
    };
  };

  createIndexes = elements => {
    let elementsIndex = {};
    let logicIndex = {};

    elements.forEach((item, index) => {
      elementsIndex[item.id] = index;

      if (!logicIndex[item.logicId]) logicIndex[item.logicId] = [index];
      else logicIndex[item.logicId].push(index);
    });

    return { elementsIndex, logicIndex };
  };

  getLookupValue = key => {
    return this.parserState.lookUps[key] || null;
  };

  setFormValues = () => {
    const { elements, elementsIndex } = this.parserState;

    elements.forEach(el => {
      const { id, logicId, isRootLevel, ignoreFormValue, origRowIndex, colIndex, isTable, parentId, props } = el;

      if (parentId) {
        const value = transformElementValue(el, props.value, { elements, elementsIndex }, false);
        this.setFormValue(logicId, value, parentId, origRowIndex, colIndex);
      } else if (isRootLevel && !isTable && !ignoreFormValue) {
        this.setFormValue(id, props.value);
      }
    });
  };

  setFormValue = (id, value, parentId, rowIndex, colIndex) => {
    if (!parentId) {
      this.parserState.formValues.dataRequests[id] = value;
      this.parserState.formValues.rawElements = this.parserState.elements;
      this.parserState.formValues.rawElementsIndex = this.parserState.elementsIndex;
      return;
    }

    if (parentId !== undefined && (rowIndex === undefined || colIndex === undefined)) {
      return;
    }

    if (this.parserState.formValues.tables[parentId] === undefined) this.parserState.formValues.tables[parentId] = {};
    if (this.parserState.formValues.tables[parentId][rowIndex] === undefined) this.parserState.formValues.tables[parentId][rowIndex] = [];

    this.parserState.formValues.rawElements = this.parserState.elements;
    this.parserState.formValues.rawElementsIndex = this.parserState.elementsIndex;

    this.parserState.formValues.tables[parentId][rowIndex][colIndex] = value;

    // tables: we will use the isUpdated value from the first column in different functions
    // to let us know if this table row has been changed. so in order to keep track we must always make sure
    // to update that first column if a user updates a different column
    const { isUpdated } = value;

    if (isUpdated) {
      this.parserState.formValues.tables[parentId][rowIndex][0].isUpdated = true;
    }
  };

  removeEmptyRowFromTableFormValues = (parentId, rowIndex) => {
    if (parentId) {
      let currParserState = this.parserState.formValues.tables[parentId];
      Object.keys(currParserState).forEach(key => {
        if (parseInt(key) === rowIndex) {
          //newParserState[key] = currParserState[key]
          currParserState[key].forEach(col => {
            col.isDeleted = true;
          });
        }
      });
      //this.parserState.formValues.tables[parentId] = newParserState;
      //return this.parserState.formValues.tables[parentId]
    }
  };
  // provide access to parser functions + outside functions via dependency injection
  elementActions = () => ({
    getParserState: () => this.parserState,
    setExactInput: this.setExactInput,
    getOverrides: () => this.props.overrideControls || {},
    setParserState: newElements => {
      this.parserState = { ...this.parserState, elements: newElements, ...this.createIndexes(newElements) };
    },
    setFormValues: () => this.setFormValues(),
    callOnChange: this.handleOnChange,
    forceParserRefresh: () => this.setState({ forceRender: true }),
    refreshShowLogic: () => {
      const { elements, elementsIndex, logicIndex } = this.parserState;
      const evaluatedElements = this.evaluateComputeLogic(elements, elementsIndex, logicIndex);
      this.applyShowLogicUpdates(elements, evaluatedElements);
      this.parserState = { ...this.parserState, elements: evaluatedElements, ...this.createIndexes(evaluatedElements) };
    },
    setComponentRef: this.setComponentRef,
    setUpdateComponentFn: this.setUpdateComponentFn,
    setConditionalFn: this.conditionalWatch,
    setFormValue: this.setFormValue,
    ...this.props.actions,
    removeEmptyRowFromTableFormValues: (parentId, rowIndex) => this.removeEmptyRowFromTableFormValues(parentId, rowIndex),
  });

  traverseElements = (pageElements, currentElements, isRootLevel = true) => {
    pageElements.forEach(node => {
      const { excludeControls } = this.props;
      const elementResult = getContentElement(node.elementType);

      if (!isElementExcluded(excludeControls, node) && elementResult) {
        const { recurse, factory } = elementResult;

        if (recurse) {
          if (!factory) {
            throw Error('ContentParser: when providing `recurse` the `factory` property must be present.');
          }

          currentElements = this.extractFactoryElements(factory, node, currentElements, isRootLevel);
        } else {
          currentElements = this.extractSimpleElements([node], currentElements, isRootLevel);
        }
      }
    });

    return currentElements;
  };

  /* 5-7-2019 - The BE has stated that dropdowns with empty values can not be sent over as '' or NULL.
   *            Because of this the FE is making a work around by simply ignoring the "'(Select)'" we are being told
   *            to send over instead of empty/null
   *
   */
  checkDownDropComplete = el => {
    const { props } = el;
    const lookUpIndex = findIndex(props.listOptions, item => item.displayText === '(Select)');

    if (lookUpIndex !== -1) {
      return props.listOptions[lookUpIndex].lookUpBindValue === props.value ? false : true;
    }

    return true;
  };

  /*
   * Check the visibilty of the parent table to see if need to be considered on the complete form validation
   *
   */
  checkParentVisibility = el => {
    const { elements } = this.parserState;
    const parentTable = elements.filter(obj => {
      return obj.id === el;
    });
    return parentTable.length > 0 ? parentTable[0].props.isComponentVisible : false;
  };

  checkFormComplete = () => {
    const { elements, elementsIndex, formValues } = this.parserState;
    let isFormComplete = true;

    const { dataRequests } = formValues;
    const elKeys = Object.keys(dataRequests);

    /* check all regular page elements that are required are filled out */
    for (let index = 0; index < elKeys.length; index++) {
      const elId = elKeys[index];
      const formVal = dataRequests[elId];
      const elIndex = elementsIndex[elId];
      const el = elements[elIndex];

      const { required, isComponentVisible, controlTypeName, reviewerOnly } = el.props;

      // In case of experian test doc id button, if button is enabled then submit button should be disabled
      // if submit button is disabled then we can enable submit button provided all mandetory fields are provided.(As existing functionality)
      if (controlTypeName === 'CrossCore' && el.props.readOnly !== true) {
        isFormComplete = false;
        break;
      }

      if (required && isComponentVisible & !reviewerOnly) {
        if (controlTypeName === 'DropDown' || controlTypeName === 'CountryList') {
          if (this.checkDownDropComplete(el)) {
            isFormComplete = this.checkDownDropComplete(el);
          } else {
            isFormComplete = false;
            break;
          }
        }
        //modified to incorporate checkbox value which is "false" if not selected
        if (formVal === '' || formVal === null || (typeof formVal === 'string' && formVal.toLowerCase() === 'false')) {
          isFormComplete = false;
          break;
        }
      }
    }

    let entityName = '';
    let rowId = -1;
    let rows = [];
    let isEntityTableValid = true;
    //Here we are ignoring
    /* check if all table fields that are required all filled out */
    for (let tableIndex = 0; tableIndex < elements.length; tableIndex++) {
      const tableElId = elements[tableIndex];
      entityName = tableElId.controlTypeName === 'Entity' ? tableElId.logicId || tableElId.id : entityName;

      // we use this to determine if it a part of a table/accordion
      //removing  tables[tableElId.parentId] conditional check because if we add first row , then tables is an empty object
      //entity table validation in else part
      if (tableElId.parentId && tableElId.parentId !== entityName) {
        entityName = '';
        if (
          this.checkParentVisibility(tableElId.parentId) &&
          tableElId.props.required &&
          !tableElId.props.reviewerOnly &&
          tableElId.props.isComponentVisible &&
          (tableElId.props.value === '' || tableElId.props.value === null || tableElId.props.value.search('Select') > -1)
        ) {
          isFormComplete = false;
          break; //loop should break even  if single element inside table is empty
        }
      } else if (tableElId.parentId && tableElId.parentId === entityName) {
        if (
          !tableElId.props.reviewerOnly &&
          tableElId.props.isComponentVisible &&
          tableElId.props.required &&
          this.checkParentVisibility(tableElId.parentId)
        ) {
          if (rowId !== tableElId.rowIndex) {
            rowId = tableElId.rowIndex;
            rows.push({ [tableElId.colIndex]: tableElId });
          } else {
            rows[rowId][tableElId.colIndex] = tableElId;
          }
        }
      }
    }

    //Entity table rows validtion
    //if atleast single row is valid, then this table is complete.
    if (rows.length > 0) {
      let isTableValid = false;
      rows.forEach(row => {
        isTableValid = Object.entries(row).filter(([key, col]) => {
          let result = col.props.required && col.props.value !== '' && col.props.value !== null && col.props.value.search('Select') === -1;
          return result;
        });
      });

      isEntityTableValid = isTableValid && Object.keys(isTableValid).length > 0;
    }

    //If other form validtion and entity table validation both are complete then complete page is complete
    isFormComplete = isFormComplete && isEntityTableValid;
    return isFormComplete;
  };

  conditionalWatch = (id, event, releatedValue = {}) => {
    const { elements, elementsIndex, logicIndex } = this.parserState;
    const position = elementsIndex[id];

    let newElements = [...elements];
    newElements[position] = { ...newElements[position], props: { ...newElements[position].props, value: event.target.value } };
    newElements[position].updateComponent({ ...newElements[position].props });

    // update formValues
    const value = transformElementValue(newElements[position], event.target.value, { elements, elementsIndex });
    const { logicId: elId, parentId, origRowIndex, colIndex } = newElements[position];

    const evaluatedElements = this.evaluateComputeLogic(newElements, elementsIndex, logicIndex);
    this.props.hasError
      ? this.applyShowLogicUpdates(elements, evaluatedElements, true, id)
      : this.applyShowLogicUpdates(elements, evaluatedElements);

    this.parserState = { ...this.parserState, elements: evaluatedElements };

    if (parentId) {
      this.setFormValue(elId, value, parentId, origRowIndex, colIndex);
    } else {
      this.setFormValue(elId, value);
    }

    const elNode = newElements[position];

    if (elNode.props.addressLookUpCountry === true) {
      let lookUpValue = this.getLookupValue(elNode.controlTypeName);
      let showLogicLookUp = '';
      if (lookUpValue) {
        showLogicLookUp = lookUpValue.reduce((acc, next) => {
          //let lookupIndex = next.lookupCode ? next.lookupCode : next.displayText;

          acc[next.lookUpBindValue] = next.lookupCode;
          return acc;
        }, {});
      }

      this.parserState = { ...this.parserState, countrySelected: showLogicLookUp[elNode.props.value] };
    }
    if (elNode.id.indexOf('ExpIDCountry') !== -1) {
      this.parserState = { ...this.parserState, sdkCountry: event.target.value };
    }
    if (elNode.id.indexOf('ExpIDType') !== -1 && elNode.props.isComponentVisible === true) {
      this.parserState = { ...this.parserState, sdkDoctype: event.target.value };
    }
    if (elNode.props.smartLookUpSearchable === true) {
      this.updateReleatedFields(releatedValue);
    }
    this.handleOnChange(elId);
    if (
      (['CheckBox', 'Radio', 'DropDown', 'CountryList'].indexOf(elNode.controlTypeName) >= 0 ||
        (elNode.controlTypeName === 'Textbox' && event.type === 'blur')) &&
      (elNode.isCallPersist === true || elNode.props.reloadPage === true) &&
      typeof this.props.actions.saveQuestionnaire === 'function'
    ) {
      this.props.actions.forceDisableSubmitBtn(true);
      if (elNode.props.reloadPage === true) {
        this.props.toggleReloadPage();
      }

      //   let isFormComplete = this.checkFormComplete()
      let isFormComplete = true;
      this.props.actions.saveQuestionnaire(() => {
        this.props.actions.forceDisableSubmitBtn(false);
      }, isFormComplete);
    }
  };

  updateReleatedFields = releatedValue => {
    this.parserState.addressFields.forEach(releatedEle => {
      if (releatedEle['smartLookUpSearchable'] !== true) {
        let value = releatedValue[releatedEle['smartAddLookupName']];
        let oldEle = this.parserState.elements[this.parserState.elementsIndex[releatedEle.elementName]];
        let oldValue = oldEle.props.value;

        let id = releatedEle['elementName'];
        let syntaticEvent = {
          target: {
            value: value === '#NODATA#' ? '' : value || oldValue || '',
          },
        };
        if (releatedEle.controlTypeName === 'DropDown' || releatedEle.controlTypeName === 'CountryList') {
          let lookUpValue = this.getLookupValue(releatedEle['lookUpName']);
          let showLogicLookUp = '';
          if (lookUpValue) {
            showLogicLookUp = lookUpValue.reduce((acc, next) => {
              //let lookupIndex = next.lookupCode ? next.lookupCode : next.displayText;
              let lookupIndex = next.displayText ? next.displayText : next.lookUpBindValue.split('|')[1];
              acc[lookupIndex] = next.lookUpBindValue;
              return acc;
            }, {});
          }
          value = showLogicLookUp[releatedValue[releatedEle['smartAddLookupName']]];
          if (!value) value = releatedEle.value || showLogicLookUp['(Select)'];

          syntaticEvent.target = { id: id, label: releatedValue[releatedEle['smartAddLookupName']], name: id, value: value };
        }

        this.conditionalWatch(id, syntaticEvent, {});
      }
    });
    // Object.keys(releatedValue).forEach((key)=>{

    // })

    // this.conditionalWatch(id, event, releatedValue = {})
  };
  applyShowLogicUpdates = (elements, newElements, forceUpdate = false, id) => {
    const hasErrorCP = this.props.hasError;
    elements.forEach((previousEl, index) => {
      const currentEl = newElements[index];

      if (previousEl.id !== currentEl.id) {
        throw Error('ContentParser: applyElementUpdates has a state mismatch. Dynamic entries could be the issue');
      }

      if (forceUpdate || previousEl.props.isComponentVisible !== currentEl.props.isComponentVisible) {
        /* THE BELOW IF STATEMENT IS THE BIGGEST I'VE EVER SEEN
         * THERE MUST BE A BETTER WAY TO DO THIS
         */
        // required field highlighting
        if (currentEl.props.id === id || !id) {
          if (hasErrorCP) {
            let hasError;
            if (
              (currentEl.props.value === '' ||
                (typeof currentEl.props.value === 'string' &&
                  currentEl.props.value.toLowerCase() === 'false' &&
                  previousEl.props.value.toLowerCase() === 'true') ||
                (currentEl.props.value &&
                  typeof currentEl.props.value === 'string' &&
                  currentEl.props.value.search('Select') > -1 &&
                  previousEl.props.value.search('Select') === -1) ||
                currentEl.props.value === null ||
                typeof currentEl.props.value === 'undefined') &&
              previousEl.props.value &&
              (previousEl.props.value.length > 0 || typeof previousEl.props.value === 'object')
            ) {
              hasError = false;
            } else {
              hasError =
                (currentEl.props.value === '' ||
                  (typeof currentEl.props.value === 'string' && currentEl.props.value.toLowerCase() === 'false') ||
                  (currentEl.props.value && typeof currentEl.props.value === 'string' && currentEl.props.value.search('Select') !== -1) ||
                  currentEl.props.value === null ||
                  typeof currentEl.props.value === 'undefined') &&
                currentEl.props.required === true
                  ? true
                  : false;
            }
            currentEl.updateComponent({ ...currentEl.props, hasError });
          } else {
            currentEl.updateComponent({ ...currentEl.props });
          }
        } else {
          currentEl.updateComponent({ ...currentEl.props });
        }
      }
    });
  };

  setUpdateComponentFn = id => fn => {
    const { elements, elementsIndex } = this.parserState;

    const newElements = cloneDeep(elements);
    const position = elementsIndex[id];

    newElements[position].updateComponent = fn;
    this.parserState = { ...this.parserState, elements: newElements };
  };

  setComponentRef = id => ref => {
    const { elements, elementsIndex } = this.parserState;

    const newElements = cloneDeep(elements);
    const position = elementsIndex[id];

    if (ref) {
      newElements[position].componentRef = ref;
    }

    this.parserState = { ...this.parserState, elements: newElements };
  };

  buildElement = (node, props, isRootLevel) => {
    return createElementEntry(
      node,
      this.props,
      isRootLevel,
      {
        getOverrides: () => this.props.overrideControls || {},
        getLookupValue: this.getLookupValue,
        conditionalWatch: this.conditionalWatch,
        setUpdateComponent: this.setUpdateComponentFn,
        setComponentRef: this.setComponentRef,
        setFormValue: this.setFormValue,
        elementActions: this.elementActions,
      },
      {
        rootNumberOfColumns: this.parserState.rootNumberOfColumns,
        readOnlyControl: this.props.readOnly,
        profileCountryCode: this.parserState.profileCountryCode,
      },
    );
  };

  extractFactoryElements = (factory, node, currentElements, isRootLevel) => {
    const factoryElements = cloneDeep(currentElements);
    const factoryResult = factory(node, this.traverseElements, currentElements, this.elementActions);

    if (!isValidFactory(factoryResult)) {
      throw Error('ContentParser: factory provided for node ', node, ' is not returning proper structure');
    }

    let factoryRootElement = this.buildElement({ ...node, readOnly: this.props.parentReadOnly }, this.props, isRootLevel);
    factoryRootElement.component = ContentControlHOC(
      factoryResult.component,
      this.setUpdateComponentFn(factoryRootElement.id),
      this.setComponentRef(factoryRootElement.id),
    );

    if (factoryResult.onInitialization) {
      this.parserState.initCallbacks.push(factoryResult.onInitialization);
    }

    factoryElements.push(factoryRootElement);
    factoryResult.elements.forEach(el => factoryElements.push(el));

    return factoryElements;
  };

  extractSimpleElements = (data, currentElements, isRootLevel) => {
    let elements = [];
    let formValues = { ...this.parserState.formValues };

    data.forEach(node => {
      const elResult = this.buildElement({ ...node, readOnly: this.props.parentReadOnly }, this.props, isRootLevel);
      if (elResult) {
        elements.push(elResult);
        formValues[elResult.id] = elResult.props.value;
      }
    });

    if (!elements.length) return currentElements;

    elements = currentElements.concat(elements);
    return elements;
  };

  evaluateComputeLogic = (elements, elementsIndex, logicIndex) => {
    let newElements = [...elements];

    elements.forEach(el => {
      if (el.showLogic) {
        let positions = logicIndex[el.logicId]; //logicIndex[el.id];
        let logicIndexPosition = undefined;

        /*
         * In order to support the following two scenarios:
         *  1) elements outside of a table that can affect all of the columns visiblity at once
         *  2) showLogic that is tied to individual rows
         *
         * We have a `logicIndex` and this logicIndex is an array of element positions.  When we land on a element
         * that is not a root level element we will run both scenarios above by providing a `parentId` which will be used
         * inside of the `computeLogic` function. Inside of this function during the traversal if it lands on an element
         * who's parent matches the parent in the iteration here then it will perform show logic tied to scenario #1 above
         *
         */
        if (el.isRootLevel === false && el.rowIndex !== undefined) {
          logicIndexPosition = parseInt(el.rowIndex);
          positions = [elementsIndex[el.id]];
        }

        const isComponentVisible = computeLogic(
          el.showLogic,
          newElements,
          logicIndex,
          logicIndexPosition,
          logicIndexPosition && el.parentId,
        );

        positions.forEach(position => {
          newElements[position] = { ...elements[position], props: { ...elements[position].props, isComponentVisible } };
        });
      }
    });

    return newElements;
  };

  renderRows = () => {
    const { elements, rootNumberOfColumns } = this.parserState;
    const { showLabels, pageId } = this.props;
    const componentRows = [];
    let processingColumns = false;
    let tempNestedComponents = null;
    let parentNumberOfColumns = null;

    function startProcessing(numberOfColumns) {
      processingColumns = true;
      tempNestedComponents = [];
      parentNumberOfColumns = numberOfColumns || rootNumberOfColumns;
    }

    function stopProcessing() {
      processingColumns = false;
      tempNestedComponents = null;
      parentNumberOfColumns = null;
    }

    elements.forEach((el, index) => {
      if (!isRootElement(el)) {
        return;
      }

      let { fullWidth, numberOfColumns } = el.props;
      const hasNumberOfColumns = typeof numberOfColumns === 'number';

      if (hasNumberOfColumns === false && (parentNumberOfColumns === 1 || rootNumberOfColumns === 1)) {
        fullWidth = true;
      }

      if (isReviewerOnly(el)) {
        if (tempNestedComponents && tempNestedComponents.length) {
          componentRows.push(
            <div key={componentRows.length} className={`content-parser-row col-num-${parentNumberOfColumns}`}>
              {tempNestedComponents}
            </div>,
          );
          stopProcessing();
          tempNestedComponents = [];
        }
        return;
      }

      // always render as question and answer format
      if (showLabels) {
        return componentRows.push(getElementComponent(el, componentRows.length, showLabels, pageId));
      }

      if (!processingColumns) {
        if (hasNumberOfColumns || (fullWidth && rootNumberOfColumns)) {
          // start processing columns from next element
          startProcessing(numberOfColumns);
          // add current row
          return componentRows.push(
            getElementComponent(el, componentRows.length, showLabels, pageId, { numOfColInfo: parentNumberOfColumns }),
          );
        }

        if (rootNumberOfColumns) {
          // start processing columns from now
          startProcessing(numberOfColumns);
          // process the column
          return tempNestedComponents.push(
            getElementComponent(el, tempNestedComponents.length, showLabels, pageId, {
              parentNumberOfColumns,
            }),
          );
        }
      }

      if (processingColumns) {
        if (fullWidth || hasNumberOfColumns) {
          // add previous component children to row
          if (tempNestedComponents.length) {
            componentRows.push(
              <div key={componentRows.length} className={`content-parser-row col-num-${parentNumberOfColumns}`}>
                {tempNestedComponents}
              </div>,
            );
          }
          if (hasNumberOfColumns || (fullWidth && rootNumberOfColumns)) {
            // continue processing current component children
            startProcessing(numberOfColumns);
          } else {
            // stop processing children from current component onward
            stopProcessing();
          }
          // add current row
          return componentRows.push(getElementComponent(el, componentRows.length, showLabels, pageId, {
            actions:this.props.actions,
            toggleReloadPage:this.props.toggleReloadPage}));
        }
        // process the column
        tempNestedComponents.push(
          getElementComponent(el, tempNestedComponents.length, showLabels, pageId, {
            parentNumberOfColumns,
          }),
        );

        // account for last element doesnt break out of columns
        if (index === elements.length - 1) {
          componentRows.push(
            <div key={componentRows.length} className={`content-parser-row col-num-${parentNumberOfColumns}`}>
              {tempNestedComponents}
            </div>,
          );
          stopProcessing();
        }
      }
    });
    return componentRows;
  };

  render() {
    const { elements } = this.parserState;
    const { debug } = this.props;

    if (!this.state.parserReady || !elements.length) return false;

    return (
      <section className="content-parser-root">
        {debug && <ContentDebugger elementActions={this.elementActions} />}
        {this.renderRows()}
      </section>
    );
  }
}

export default ContentParser;



// WEBPACK FOOTER //
// ./src/ui/library/ContentParser/ContentParser.js